<template>
  <svg
    class="app-icon"
    :class="[`app-icon--${name}`, { 'app-icon--spin': spin }]"
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    :stroke="solid ? 'none' : 'currentColor'"
    :fill-rule="solid ? 'evenodd' : undefined"
    stroke-width="1.75"
    stroke-linecap="round"
    stroke-linejoin="round"
    :role="label ? 'img' : undefined"
    :aria-label="label || undefined"
    :aria-hidden="label ? undefined : 'true'"
    focusable="false"
  >
    <title v-if="label">{{ label }}</title>
    <template v-for="(d, i) in paths" :key="i">
      <circle
        v-if="d.circle"
        :cx="d.circle[0]"
        :cy="d.circle[1]"
        :r="d.circle[2]"
        :fill="d.fill || 'none'"
        :stroke-dasharray="d.dash || undefined"
      />
      <path
        v-else
        :d="d.d"
        :fill="d.fill || 'none'"
        :stroke="d.stroke || undefined"
        :stroke-dasharray="d.dash || undefined"
      />
    </template>
  </svg>
</template>

<script>
// ---------------------------------------------------------------------------
// One icon system.
//
// The set this replaces was drawn by nobody in particular: six grids (24, 32,
// 40, 50, 64, 92 and two 800px SVGs scraped from svgrepo), four fill
// philosophies, and three palettes — green, yellow and blue — in a cabinet
// whose brand colour is orange. Two of them baked English text into a 40px
// bitmap.
//
// Everything below is on ONE 24px grid at ONE stroke weight, drawn in
// `currentColor` so a single definition serves brand orange, navy ink, muted
// grey and white-on-dark without a second file. Size with the `size` prop,
// colour with CSS `color`.
// ---------------------------------------------------------------------------
const ICONS = {
  // Navigation ------------------------------------------------------------
  "arrow-left": [{ d: "M19 12H5" }, { d: "M11 18l-6-6 6-6" }],
  "arrow-right": [{ d: "M5 12h14" }, { d: "M13 6l6 6-6 6" }],
  "chevron-down": [{ d: "M6 9.5l6 6 6-6" }],

  // Documents -------------------------------------------------------------
  // The doc shape is shared by every document icon so they read as one family.
  document: [
    { d: "M14 2.75H7A2.25 2.25 0 0 0 4.75 5v14A2.25 2.25 0 0 0 7 21.25h10A2.25 2.25 0 0 0 19.25 19V8z" },
    { d: "M14 2.75V8h5.25" }
  ],
  // Signed / proxy required — the doc plus a mark of assent.
  "document-signed": [
    { d: "M14 2.75H7A2.25 2.25 0 0 0 4.75 5v14A2.25 2.25 0 0 0 7 21.25h10A2.25 2.25 0 0 0 19.25 19V8z" },
    { d: "M14 2.75V8h5.25" },
    { d: "M8.5 15.25l2.25 2.25 4.75-5" }
  ],
  download: [
    { d: "M12 3.75v10.5" },
    { d: "M7.75 10l4.25 4.25L16.25 10" },
    { d: "M4.75 19.25h14.5" }
  ],
  eye: [
    { d: "M2.75 12S6.25 6.25 12 6.25 21.25 12 21.25 12 17.75 17.75 12 17.75 2.75 12 2.75 12Z" },
    { circle: [12, 12, 3] }
  ],
  // Viewer controls. Four corner brackets rather than diagonal arrows: the
  // arrows read as "move" at 20px, the brackets read as "frame".
  maximize: [
    { d: "M9.25 3.75H5.5a1.75 1.75 0 0 0-1.75 1.75v3.75" },
    { d: "M20.25 9.25V5.5a1.75 1.75 0 0 0-1.75-1.75h-3.75" },
    { d: "M3.75 14.75v3.75a1.75 1.75 0 0 0 1.75 1.75h3.75" },
    { d: "M14.75 20.25h3.75a1.75 1.75 0 0 0 1.75-1.75v-3.75" }
  ],
  minimize: [
    { d: "M9.25 3.75V7.5a1.75 1.75 0 0 1-1.75 1.75H3.75" },
    { d: "M20.25 9.25H16.5a1.75 1.75 0 0 1-1.75-1.75V3.75" },
    { d: "M3.75 14.75H7.5a1.75 1.75 0 0 1 1.75 1.75v3.75" },
    { d: "M14.75 20.25V16.5a1.75 1.75 0 0 1 1.75-1.75h3.75" }
  ],

  // Status ----------------------------------------------------------------
  "alert-circle": [
    { circle: [12, 12, 9.25] },
    { d: "M12 7.5v5" },
    { d: "M12 16.25h.01" }
  ],
  warning: [
    { d: "M10.7 3.9a1.5 1.5 0 0 1 2.6 0l7.7 13.35a1.5 1.5 0 0 1-1.3 2.25H4.3a1.5 1.5 0 0 1-1.3-2.25z" },
    { d: "M12 9.5v4" },
    { d: "M12 16.75h.01" }
  ],
  "check-circle": [
    { circle: [12, 12, 9.25] },
    { d: "M8 12.25l2.75 2.75L16.25 9.5" }
  ],
  // Pending — the dashed ring says "not finished yet" without a second colour.
  pending: [
    { circle: [12, 12, 9.25], dash: "2.2 2.6" },
    { d: "M12 7v5.25l3.25 1.9" }
  ],
  // Buy-back guarantee. Was a green bitmap badge with "Buy back" baked in.
  "shield-check": [
    { d: "M12 2.75l7.25 3v6.1c0 4.2-2.9 7.4-7.25 8.9-4.35-1.5-7.25-4.7-7.25-8.9v-6.1z" },
    { d: "M8.75 12l2.25 2.25 4.25-4.5" }
  ],

  // Money / project -------------------------------------------------------
  wallet: [
    { d: "M3.75 7.5A2.75 2.75 0 0 1 6.5 4.75h11A1.75 1.75 0 0 1 19.25 6.5v1.25" },
    { d: "M3.75 7.5v10A2.75 2.75 0 0 0 6.5 20.25h11.75a1.5 1.5 0 0 0 1.5-1.5v-8.5a1.5 1.5 0 0 0-1.5-1.5H6.5" },
    { d: "M16 14.25h.01" }
  ],
  building: [
    { d: "M4.75 20.25V5.5a.75.75 0 0 1 .75-.75h8a.75.75 0 0 1 .75.75v14.75" },
    { d: "M14.25 10.25h4a.75.75 0 0 1 .75.75v9.25" },
    { d: "M3 20.25h18" },
    { d: "M8 8.75h2.5" },
    { d: "M8 12.75h2.5" },
    { d: "M8 16.75h2.5" }
  ],

  // Third-party -----------------------------------------------------------
  // Telegram's own mark. Flattened: the file this replaces carried a
  // linear-gradient, which the house rules ban outright.
  telegram: [
    { circle: [12, 12, 9.25] },
    { d: "M7.3 12.1l8.4-3.3a.4.4 0 0 1 .53.46l-1.35 6.6a.4.4 0 0 1-.64.23l-2.1-1.6-1.1 1.07a.35.35 0 0 1-.6-.2l-.3-2.3" },
    { d: "M9.8 13.06l5.1-3.4" }
  ],

  // Motion ----------------------------------------------------------------
  // A 270° arc; the spin comes from CSS so it honours reduced-motion.
  spinner: [{ d: "M12 2.75a9.25 9.25 0 1 0 9.25 9.25" }]
};

export default {
  name: "AppIcon",
  props: {
    name: {
      type: String,
      required: true,
      validator: (v) => Object.prototype.hasOwnProperty.call(ICONS, v)
    },
    // Icons sit on the text baseline, so the default matches the 1rem body.
    size: { type: [Number, String], default: 20 },
    // Accessible name. Leave empty for a decorative icon next to real text —
    // it is then hidden from assistive tech rather than read out twice.
    label: { type: String, default: "" },
    spin: { type: Boolean, default: false },
    solid: { type: Boolean, default: false }
  },
  computed: {
    paths() {
      return ICONS[this.name] || [];
    }
  }
};
</script>

<style lang="scss">
.app-icon {
  display: inline-block;
  vertical-align: -0.185em;
  flex: none;
  // Several call sites inherit legacy CSS that sized the <img> this replaces
  // and set only `width`. Every icon is drawn square on a 24 grid, so pin the
  // ratio rather than letting those rules letterbox the glyph.
  height: auto;
  aspect-ratio: 1;
}

.app-icon--spin {
  animation: app-icon-spin 800ms linear infinite;
  transform-origin: 50% 50%;
}

@keyframes app-icon-spin {
  to {
    transform: rotate(360deg);
  }
}

// app.scss already clamps every animation under `prefers-reduced-motion`, but
// a spinner frozen mid-arc reads as a broken glyph. Hide the rotation and let
// the arc sit as a static ring instead.
@media (prefers-reduced-motion: reduce) {
  .app-icon--spin {
    animation: none;
    opacity: 0.6;
  }
}
</style>
